import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

const cycle = (plan) =>
  plan ? `£${plan.amount.toFixed(2)} every ${plan.interval_count > 1 ? `${plan.interval_count} ` : ""}${plan.interval}${plan.interval_count > 1 ? "s" : ""}` : "";

/** Premium subscription card: 30-day free trial, live trial status and cancel-before-renewal. */
export const PremiumPanel = ({ onChange }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/payments/premium-status").then((r) => setS(r.data)).catch(() => setS(null));
  useEffect(() => { load(); }, []);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/premium-checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/premium-cancel", {});
      toast.success(`Cancelled. Premium ends ${fmtDate(data.ends_at)} — you won't be charged again.`);
      await load();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!s || !s.available) return null;

  if (s.premium) {
    return (
      <div className="rounded-2xl bg-accent/10 border border-accent/30 p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="premium-active">
        <Crown className="h-6 w-6 text-accent shrink-0" />
        <div className="min-w-0">
          <p className="font-semibold" data-testid="premium-state-title">
            {s.trial_active ? `Premium free trial active — ${s.trial_days_left} day${s.trial_days_left === 1 ? "" : "s"} left` : "Premium active"}
          </p>
          <p className="text-sm text-muted-foreground mt-0.5" data-testid="premium-state-note">
            {s.cancel_at_period_end
              ? `Cancelled — your Premium ends ${fmtDate(s.ends_at)} and you won't be charged again.`
              : s.trial_active
                ? `£0 charged today. Your trial ends ${fmtDate(s.trial_ends_at)}, then ${cycle(s.plan)} unless you cancel before renewal.`
                : `You're ranked first with zero service fees. Renews ${fmtDate(s.renews_at)} · ${cycle(s.plan)}.`}
          </p>
        </div>
        {!s.cancel_at_period_end && (
          <button onClick={cancel} disabled={busy}
            className="sm:ml-auto shrink-0 px-5 py-2.5 rounded-full border border-accent/40 text-sm font-medium hover:bg-accent/10 transition-colors disabled:opacity-50"
            data-testid="premium-cancel-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : s.trial_active ? "Cancel free trial" : "Cancel subscription"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-primary text-background p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="premium-upsell">
      <div>
        <div className="inline-flex items-center gap-2 text-accent font-semibold">
          <Crown className="h-5 w-5" /> {s.trial_eligible ? `${s.trial_days} days free` : "Go Premium"}
        </div>
        <h3 className="font-display text-2xl font-medium mt-2">
          {s.trial_eligible ? `Try Premium free for ${s.trial_days} days` : `Rank first. Zero fees. ${cycle(s.plan)}`}
        </h3>
        <p className="text-background/70 mt-1 text-sm" data-testid="premium-offer-note">
          Top of search results, a verified Premium badge and no £1.99 service fee on your bookings.
          {s.trial_eligible
            ? ` £0 today — after ${s.trial_days} days it continues at ${cycle(s.plan)} unless you cancel before renewal.`
            : ""}
        </p>
      </div>
      <button onClick={start} disabled={busy}
        className="px-6 py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors whitespace-nowrap disabled:opacity-50"
        data-testid={s.trial_eligible ? "start-premium-trial-btn" : "go-premium-btn"}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : s.trial_eligible ? `Start ${s.trial_days}-day free trial` : "Upgrade now"}
      </button>
    </div>
  );
};

export default PremiumPanel;
